import {
  AppstoreOutlined,
  AudioOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileUnknownOutlined,
  FileWordOutlined,
  FileZipOutlined,
  LockOutlined,
  MoreOutlined,
  SafetyCertificateOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Progress,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useMemo, useRef, useState } from "react";

import {
  completeUpload,
  createDownloadTicket,
  createPreviewTicket,
  createUploadIntent,
  deleteAttachment,
  fetchProjectAttachments,
  renameAttachment,
  uploadToIntent,
  type Attachment,
  type AttachmentTicket,
} from "../../api/attachments";
import { ApiError } from "../../api/client";

const PROJECT_ACCEPT = ".png,.jpg,.webp,.mp3,.mp4,.wav,.rar,.zip,.docx,.doc,.pdf,.xls,.xlsx";
const PROJECT_EXTENSIONS = new Set(PROJECT_ACCEPT.split(",").map((item) => item.slice(1)));
const MAX_ATTACHMENT_COUNT = 20;
const MAX_ATTACHMENT_SIZE = 100 * 1024 * 1024;
const STATE_LABELS = {
  UPLOADING: { label: "待上传", color: "default" },
  QUARANTINED: { label: "文件校验中", color: "processing" },
  ACTIVE: { label: "可用", color: "success" },
  REJECTED: { label: "已拒绝", color: "error" },
} as const;

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : fallback;
}

function extensionOf(name: string) {
  return name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
}

function formatSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatUploadedAt(value: string | null) {
  if (!value) return "等待上传";
  return new Date(value).toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function attachmentIcon(extension: string) {
  const props = { "aria-hidden": true };
  if (["png", "jpg", "webp"].includes(extension)) return <FileImageOutlined {...props} />;
  if (extension === "pdf") return <FilePdfOutlined {...props} />;
  if (["doc", "docx"].includes(extension)) return <FileWordOutlined {...props} />;
  if (["xls", "xlsx"].includes(extension)) return <FileExcelOutlined {...props} />;
  if (["zip", "rar"].includes(extension)) return <FileZipOutlined {...props} />;
  if (["mp3", "wav"].includes(extension)) return <AudioOutlined {...props} />;
  if (extension === "mp4") return <VideoCameraOutlined {...props} />;
  return <FileUnknownOutlined {...props} />;
}

interface ProjectAttachmentWorkspaceProps {
  projectId: string;
  canUpload: boolean;
  canManage: boolean;
  attachments?: Attachment[];
}

export function ProjectAttachmentWorkspace({
  projectId,
  canUpload,
  canManage,
  attachments,
}: ProjectAttachmentWorkspaceProps) {
  const { message, modal } = App.useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [search, setSearch] = useState("");
  const [extensionFilter, setExtensionFilter] = useState<string>();
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest" | "name">("newest");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<{
    attachment: Attachment;
    ticket: AttachmentTicket;
  } | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [renaming, setRenaming] = useState<Attachment | null>(null);
  const [nextName, setNextName] = useState("");

  const query = useQuery({
    queryKey: ["project-attachments", projectId],
    queryFn: () => fetchProjectAttachments(projectId),
    enabled: attachments === undefined,
    refetchInterval: (result) =>
      result.state.data?.some((item) => ["UPLOADING", "QUARANTINED"].includes(item.state))
        ? 1500
        : false,
  });
  const items = useMemo(() => attachments ?? query.data ?? [], [attachments, query.data]);
  const totalSize = items.reduce((total, item) => total + item.size_bytes, 0);
  const extensionOptions = useMemo(
    () =>
      Array.from(new Set(items.map((item) => item.extension)))
        .sort()
        .map((extension) => ({ value: extension, label: extension.toUpperCase() })),
    [items],
  );
  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = items.filter(
      (item) =>
        (!term || item.display_name.toLowerCase().includes(term)) &&
        (!extensionFilter || item.extension === extensionFilter),
    );
    return [...filtered].sort((left, right) => {
      if (sortOrder === "name") return left.display_name.localeCompare(right.display_name, "zh-CN");
      const leftTime = new Date(left.uploaded_at ?? left.created_at).getTime();
      const rightTime = new Date(right.uploaded_at ?? right.created_at).getTime();
      return sortOrder === "oldest" ? leftTime - rightTime : rightTime - leftTime;
    });
  }, [extensionFilter, items, search, sortOrder]);

  const uploadFiles = async (files: File[]) => {
    if (!files.length || uploading) return;
    const availableSlots = Math.max(0, MAX_ATTACHMENT_COUNT - items.length);
    const accepted = files.slice(0, availableSlots).filter((file) => {
      const extension = extensionOf(file.name);
      if (!PROJECT_EXTENSIONS.has(extension)) {
        message.error(`${file.name} 的文件格式不支持`);
        return false;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        message.error(`${file.name} 超过 100 MB`);
        return false;
      }
      return true;
    });
    if (files.length > availableSlots) {
      message.warning(`单项目最多上传 ${MAX_ATTACHMENT_COUNT} 个附件`);
    }
    if (!accepted.length) return;

    setUploading(true);
    setUploadProgress(0);
    const failed: string[] = [];
    for (const [index, file] of accepted.entries()) {
      try {
        const intent = await createUploadIntent({ projectId }, file);
        await uploadToIntent(intent, file, (fileProgress) => {
          setUploadProgress(Math.round(((index + fileProgress / 100) / accepted.length) * 100));
        });
        await completeUpload(intent.attachment.id);
      } catch {
        failed.push(file.name);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    if (inputRef.current) inputRef.current.value = "";
    await query.refetch();
    if (failed.length) {
      message.warning(`${failed.length} 个附件上传失败，请重新上传`);
    } else {
      message.success("附件上传完成，正在进行安全校验");
    }
  };

  const view = async (attachment: Attachment) => {
    try {
      const ticket = await createPreviewTicket(attachment.id);
      setPreviewFailed(false);
      setPreviewLoading(true);
      setPreview({ attachment, ticket });
    } catch (error) {
      message.error(errorMessage(error, "预览授权失败"));
    }
  };
  const download = async (attachment: Attachment) => {
    try {
      const ticket = await createDownloadTicket(attachment.id);
      window.location.assign(ticket.url);
    } catch (error) {
      message.error(errorMessage(error, "下载授权失败"));
    }
  };
  const failPreviewLoad = () => {
    setPreviewLoading(false);
    setPreviewFailed(true);
  };
  const saveRename = async () => {
    if (!renaming || !nextName.trim()) return;
    try {
      await renameAttachment(renaming.id, nextName.trim());
      setRenaming(null);
      message.success("附件名称已更新");
      await query.refetch();
    } catch (error) {
      message.error(errorMessage(error, "重命名失败"));
    }
  };
  const remove = async (attachment: Attachment) => {
    try {
      await deleteAttachment(attachment.id);
      message.success("附件已删除");
      await query.refetch();
    } catch (error) {
      message.error(errorMessage(error, "附件删除失败"));
    }
  };
  const openRename = (attachment: Attachment) => {
    setRenaming(attachment);
    setNextName(attachment.display_name);
  };
  const confirmRemove = (attachment: Attachment) => {
    modal.confirm({
      title: `删除 ${attachment.display_name}？`,
      content: "删除后立即停止签发新的查看和下载票据。",
      okText: "删除",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: () => remove(attachment),
    });
  };
  const renderPrimaryActions = (attachment: Attachment) => (
    <div className="project-attachment-actions">
      {attachment.state === "ACTIVE" && attachment.can_preview ? (
        <Button size="small" icon={<EyeOutlined />} onClick={() => void view(attachment)}>
          查看
        </Button>
      ) : null}
      {attachment.state === "ACTIVE" ? (
        <Button size="small" icon={<DownloadOutlined />} onClick={() => void download(attachment)}>
          下载
        </Button>
      ) : null}
    </div>
  );
  const renderManagementMenu = (attachment: Attachment) => {
    if (!canManage) return null;
    return (
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        menu={{
          items: [
            ...(attachment.state === "ACTIVE"
              ? [{ key: "edit", icon: <EditOutlined />, label: "编辑文件名" }]
              : []),
            { key: "delete", danger: true, icon: <DeleteOutlined />, label: "删除附件" },
          ],
          onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === "edit") openRename(attachment);
            if (key === "delete") confirmRemove(attachment);
          },
        }}
      >
        <Button
          className="project-attachment-more"
          type="text"
          size="small"
          icon={<MoreOutlined />}
          aria-label={`${attachment.display_name} 更多操作`}
          onClick={(event) => event.stopPropagation()}
        />
      </Dropdown>
    );
  };

  return (
    <section className="project-attachment-workspace" aria-label="项目附件">
      <header className="project-attachment-heading">
        <div>
          <Typography.Title level={3}>项目附件</Typography.Title>
          <Typography.Text type="secondary">
            共 {items.length} 个文件 · {formatSize(totalSize)}
          </Typography.Text>
        </div>
        {!canUpload && !canManage ? (
          <span className="project-attachment-readonly">
            <LockOutlined /> 仅支持查看和下载
          </span>
        ) : null}
        {canUpload ? (
          <>
            <input
              ref={inputRef}
              className="visually-hidden"
              type="file"
              accept={PROJECT_ACCEPT}
              multiple
              onChange={(event) => void uploadFiles(Array.from(event.target.files ?? []))}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={uploading}
              disabled={items.length >= MAX_ATTACHMENT_COUNT}
              onClick={() => inputRef.current?.click()}
            >
              上传附件
            </Button>
          </>
        ) : null}
      </header>

      {canUpload ? (
        <button
          className={`project-attachment-dropzone${dragging ? " is-dragging" : ""}`}
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setDragging(false);
            }
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            void uploadFiles(Array.from(event.dataTransfer.files));
          }}
        >
          <UploadOutlined />
          <span>
            <strong>拖拽文件到此处，或点击选择文件</strong>
            <small>支持图片、音频、视频、PDF、Word、Excel 和压缩包</small>
          </span>
        </button>
      ) : null}

      {uploading ? <Progress percent={uploadProgress} status="active" size="small" /> : null}
      {attachments === undefined && query.isError ? (
        <Alert type="error" showIcon title="附件列表读取失败" />
      ) : null}

      <div className="project-attachment-toolbar">
        <Segmented
          value={viewMode}
          options={[
            { value: "grid", label: "网格", icon: <AppstoreOutlined /> },
            { value: "list", label: "列表", icon: <UnorderedListOutlined /> },
          ]}
          onChange={(value) => setViewMode(value as "grid" | "list")}
        />
        <Select
          allowClear
          placeholder="全部类型"
          value={extensionFilter}
          options={extensionOptions}
          onChange={setExtensionFilter}
        />
        <Select
          value={sortOrder}
          options={[
            { value: "newest", label: "按上传时间（新到旧）" },
            { value: "oldest", label: "按上传时间（旧到新）" },
            { value: "name", label: "按文件名称" },
          ]}
          onChange={setSortOrder}
        />
        <Input
          allowClear
          value={search}
          placeholder="搜索附件名称"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {(attachments !== undefined || !query.isPending) && !visibleItems.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={items.length ? "没有匹配的附件" : "暂无附件"}
        />
      ) : null}

      {visibleItems.length && viewMode === "grid" ? (
        <div className="project-attachment-grid">
          {visibleItems.map((attachment) => {
            const state = STATE_LABELS[attachment.state];
            return (
              <article className="project-attachment-card" key={attachment.id}>
                <div className="project-attachment-card-menu">
                  {renderManagementMenu(attachment)}
                </div>
                <div className={`project-attachment-icon file-icon-${attachment.extension}`}>
                  {attachmentIcon(attachment.extension)}
                </div>
                <Tooltip title={attachment.display_name}>
                  <strong>{attachment.display_name}</strong>
                </Tooltip>
                <small>{formatSize(attachment.size_bytes)}</small>
                <small>{formatUploadedAt(attachment.uploaded_at)}</small>
                {attachment.state !== "ACTIVE" ? (
                  <Tag color={state.color}>{state.label}</Tag>
                ) : null}
                {attachment.rejection_reason ? (
                  <Typography.Text type="danger">{attachment.rejection_reason}</Typography.Text>
                ) : null}
                {renderPrimaryActions(attachment)}
              </article>
            );
          })}
        </div>
      ) : null}

      {visibleItems.length && viewMode === "list" ? (
        <Table<Attachment>
          className="project-attachment-table"
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={visibleItems}
          scroll={{ x: 900 }}
          columns={[
            {
              title: "文件名称",
              dataIndex: "display_name",
              ellipsis: true,
              render: (value: string, attachment) => (
                <span className="project-attachment-file-name">
                  <i className={`file-icon-${attachment.extension}`}>
                    {attachmentIcon(attachment.extension)}
                  </i>
                  <span>{value}</span>
                </span>
              ),
            },
            {
              title: "类型",
              dataIndex: "extension",
              width: 82,
              render: (value: string) => value.toUpperCase(),
            },
            {
              title: "文件大小",
              dataIndex: "size_bytes",
              width: 100,
              render: formatSize,
            },
            {
              title: "上传时间",
              dataIndex: "uploaded_at",
              width: 165,
              render: formatUploadedAt,
            },
            {
              title: "状态",
              dataIndex: "state",
              width: 105,
              render: (value: Attachment["state"]) => {
                const state = STATE_LABELS[value];
                return <Tag color={state.color}>{state.label}</Tag>;
              },
            },
            {
              title: "操作",
              key: "actions",
              width: canManage ? 210 : 150,
              render: (_, attachment) => (
                <Space size={6} wrap={false}>
                  {renderPrimaryActions(attachment)}
                  {renderManagementMenu(attachment)}
                </Space>
              ),
            },
          ]}
        />
      ) : null}

      <div className="project-attachment-audit-note">
        <SafetyCertificateOutlined />
        附件查看与下载仅可通过操作按钮执行，系统将记录操作人员、时间、IP及文件信息
      </div>

      <Modal
        title={preview?.attachment.display_name ?? "附件预览"}
        width="min(1080px, 92vw)"
        open={Boolean(preview)}
        footer={null}
        destroyOnHidden
        onCancel={() => {
          setPreview(null);
          setPreviewLoading(false);
        }}
      >
        <div className="attachment-preview-stage" aria-busy={previewLoading}>
          <Spin spinning={previewLoading} size="large" tip="文件加载中...">
            {previewFailed ? (
              <Alert
                type="error"
                showIcon
                title="预览加载失败或票据已过期"
                description="请关闭后重新点击查看。"
              />
            ) : null}
            {preview?.ticket.preview_kind === "image" ? (
              <img
                className="attachment-image-preview"
                src={preview.ticket.url}
                alt={preview.attachment.display_name}
                onLoad={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            ) : null}
            {preview?.ticket.preview_kind === "audio" ? (
              <audio
                controls
                autoPlay
                src={preview.ticket.url}
                onCanPlay={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            ) : null}
            {preview?.ticket.preview_kind === "video" ? (
              <video
                className="attachment-video-preview"
                controls
                autoPlay
                src={preview.ticket.url}
                onCanPlay={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            ) : null}
            {preview && ["pdf", "office"].includes(preview.ticket.preview_kind ?? "") ? (
              <iframe
                className="attachment-document-preview"
                title={`${preview.attachment.display_name} 在线预览`}
                src={preview.ticket.url}
                onLoad={() => setPreviewLoading(false)}
                onError={failPreviewLoad}
              />
            ) : null}
          </Spin>
        </div>
      </Modal>

      <Modal
        title="重命名附件"
        open={Boolean(renaming)}
        okText="保存"
        cancelText="取消"
        onOk={() => void saveRename()}
        onCancel={() => setRenaming(null)}
      >
        <Typography.Paragraph type="secondary">
          扩展名由系统保留，原始文件名和存储对象键不会变化。
        </Typography.Paragraph>
        <Input
          value={nextName}
          maxLength={255}
          onChange={(event) => setNextName(event.target.value)}
          onPressEnter={() => void saveRename()}
        />
      </Modal>
    </section>
  );
}
